import { useState } from 'react';
import {
  Stack,
  Paper,
  Group,
  Text,
  TextInput,
  Select,
  SimpleGrid,
  Card,
  Badge,
  Button,
  Skeleton,
  Progress,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconArrowsExchange,
  IconShoppingCartPlus,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useSubstitutes, useAddToCart } from '@/hooks/api';
import { useProducts } from '@/hooks/api';

const REASON_OPTIONS = [
  { value: 'out_of_stock', label: 'Out of Stock' },
  { value: 'price', label: 'Better Price' },
  { value: 'dietary', label: 'Dietary Needs' },
  { value: 'preference', label: 'Preference' },
];

export default function SubstitutionFinder() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(0);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [reason, setReason] = useState<string | null>('out_of_stock');

  const { data: productsData } = useProducts(
    searchQuery.length >= 2 ? { search: searchQuery, limit: 10 } : undefined,
  );

  const { data: substitutes, isLoading: loadingSubs } = useSubstitutes(
    selectedProductId,
    reason || undefined,
  );

  const addToCart = useAddToCart();

  const handleSelectProduct = (productId: number, productName: string) => {
    setSelectedProductId(productId);
    setSelectedProductName(productName);
  };

  const handleAddItem = async (productId: number, productName: string) => {
    try {
      await addToCart.mutateAsync({ product_id: productId, quantity: 1 });
      notifications.show({
        title: 'Added to Cart',
        message: `${productName} added to cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart',
        color: 'red',
      });
    }
  };

  const scoreColor = (score: number) =>
    score >= 80 ? 'green' : score >= 50 ? 'yellow' : 'red';

  return (
    <Stack gap="lg">
      <PageHeader
        title="Substitution Finder"
        description="Find alternative products when you need options"
      />

      {/* Search */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group grow>
            <TextInput
              label="Search for a Product"
              placeholder="Type a product name..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />
            <Select
              label="Reason"
              data={REASON_OPTIONS}
              value={reason}
              onChange={setReason}
              style={{ maxWidth: 200 }}
            />
          </Group>

          {/* Search Results */}
          {searchQuery.length >= 2 && productsData?.items && productsData.items.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Select a product:</Text>
              {productsData.items.map((p) => (
                <Paper
                  key={p.id}
                  p="sm"
                  radius="md"
                  withBorder
                  onClick={() => handleSelectProduct(p.id, p.name)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedProductId === p.id
                        ? 'var(--mantine-color-brand-0)'
                        : undefined,
                  }}
                >
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>{p.name}</Text>
                      <Text size="xs" c="dimmed">SKU: {p.sku}</Text>
                    </div>
                    <Text size="sm" fw={600}>${p.price}</Text>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}

          {selectedProductId > 0 && (
            <Badge size="lg" variant="light" color="blue">
              Finding substitutes for: {selectedProductName}
            </Badge>
          )}
        </Stack>
      </Paper>

      {/* Results */}
      {loadingSubs && selectedProductId > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          <Skeleton height={160} radius="md" />
          <Skeleton height={160} radius="md" />
          <Skeleton height={160} radius="md" />
        </SimpleGrid>
      )}

      {substitutes && substitutes.substitutes && substitutes.substitutes.length > 0 ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {substitutes.substitutes.map((sub) => (
            <Card key={sub.product_id} shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600} size="sm">{sub.product_name}</Text>
                  <Badge color={scoreColor(sub.match_score)} variant="light" size="sm">
                    {sub.match_score}% match
                  </Badge>
                </Group>
                {sub.sku && (
                  <Text size="xs" c="dimmed">SKU: {sub.sku}</Text>
                )}
                <Progress
                  value={sub.match_score}
                  color={scoreColor(sub.match_score)}
                  size="sm"
                />
                <Text size="xs" c="dimmed">{sub.reason}</Text>
                <Group justify="space-between" mt="xs">
                  <Text fw={600}>${sub.unit_price.toFixed(2)}</Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconShoppingCartPlus size={14} />}
                    onClick={() => handleAddItem(sub.product_id, sub.product_name)}
                    loading={addToCart.isPending}
                  >
                    Add to Cart
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      ) : selectedProductId > 0 && !loadingSubs ? (
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="No Substitutes Found"
            description="We couldn't find any alternative products. Try a different search or reason."
            icon={<IconArrowsExchange size={48} />}
          />
        </Paper>
      ) : !selectedProductId ? (
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="Search for a Product"
            description="Type a product name above and select it to find substitutes."
            icon={<IconArrowsExchange size={48} />}
          />
        </Paper>
      ) : null}
    </Stack>
  );
}
